import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { SimpleSlideNavButtonNext, SimpleSlideNavButtonPrev, SimpleSlideNavButtonSizes } from './SimpleSlideNavButton';
import './styles.scss';

interface SimpleSlideProps {
    sizeOfNavButtons?: SimpleSlideNavButtonSizes;
}
interface ScrollDataState {
    isScrolled: boolean;
    isScrollEnded: boolean;
    hasScroll: boolean;
}

const getScrollDataFrom = ({ scrollLeft, scrollWidth, clientWidth }: HTMLElement): ScrollDataState => {
    const isScrolled = scrollLeft > 0;
    const isScrollEnded = scrollWidth - scrollLeft <= clientWidth;
    const hasScroll = scrollWidth > clientWidth;
    return { isScrolled, isScrollEnded, hasScroll };
};

const SimpleHorizontalSlider: React.FC<SimpleSlideProps> = ({ children, sizeOfNavButtons }) => {
    const [scrollData, setScrollData] = useState<ScrollDataState>({ isScrollEnded: false, isScrolled: false, hasScroll: false });
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleOnScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
        setScrollData(getScrollDataFrom(event.target as HTMLElement));
    }, []);

    const onScrollNext = useCallback(() => {
        scrollRef.current?.scrollTo({
            left: scrollRef.current?.scrollLeft + 200,
            behavior: 'smooth',
        });
    }, []);

    const onScrollPrev = useCallback(() => {
        scrollRef.current?.scrollTo({
            left: scrollRef.current?.scrollLeft - 200,
            behavior: 'smooth',
        });
    }, []);

    useLayoutEffect(() => {
        if (!scrollRef.current) {
            return;
        }

        setScrollData(getScrollDataFrom(scrollRef.current));
    }, [children]);

    return (
        <div className="simple-h-slider-container">
            <div className={`simple-h-nav-container prev ${!scrollData.isScrolled && 'hide'}`}>
                <SimpleSlideNavButtonPrev disabled={!scrollData.isScrolled} onClick={onScrollPrev} size={sizeOfNavButtons} />
            </div>

            <div className="slider-content-container" onScroll={handleOnScroll} ref={scrollRef}>
                {children}
            </div>

            <div className={`simple-h-nav-container next ${(scrollData.isScrollEnded || !scrollData.hasScroll) && 'hide'}`}>
                <SimpleSlideNavButtonNext
                    disabled={scrollData.isScrollEnded || !scrollData.hasScroll}
                    onClick={onScrollNext}
                    size={sizeOfNavButtons}
                />
            </div>
        </div>
    );
};

export { SimpleHorizontalSlider };
